import { useState, useMemo, useCallback, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRoleAccess } from '../../users/hooks/useRoleAccess';
import { Plus, Search } from 'lucide-react';
import { getBadgeColorClasses, getStatusColor, NumberUtils, DateUtils, SearchBar, StatusFilters, SortableHeader, Pagination } from '@cleanpro/common-ui';
import { useJobs } from '../hooks/useJobs';

export default function Jobs() {
  const permissions = useRoleAccess();
  const navigate = useNavigate();
  const { jobs, loading, error } = useJobs();
  
  console.log('Jobs.tsx render:', { jobsCount: jobs?.length, loading, error, jobs });

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const ITEMS_PER_PAGE = 25;

  const invoices = useMemo(() => [], []);

  const { filteredJobs, filterCounts } = useMemo(() => {
    const normalizedSearch = deferredSearchTerm.trim().toLowerCase();
    const counts: Record<'all' | 'requested' | 'scheduled' | 'in-progress' | 'completed' | 'cancelled' | 'overdue' | 'needs_invoicing', number> = {
      all: 0,
      requested: 0,
      scheduled: 0,
      'in-progress': 0,
      completed: 0,
      cancelled: 0,
      overdue: 0,
      needs_invoicing: 0,
    };

    const matches = jobs.filter((job) => {
      const haystacks = [
        job.customer?.name || '',
        job.service?.name || ''
      ];

      const matchesSearch =
        normalizedSearch.length === 0 ||
        haystacks.some((value) => value.toLowerCase().includes(normalizedSearch));

      if (!matchesSearch) {
        return false;
      }

      counts.all += 1;
      if (counts[job.status as keyof typeof counts] !== undefined) {
        counts[job.status as keyof typeof counts] += 1;
      }

      // Check for overdue jobs
      const jobDate = new Date(job.date_time);
      const today = new Date();
      if (jobDate < today && !['completed', 'cancelled'].includes(job.status)) {
        counts.overdue += 1;
      }

      // Check for jobs needing invoicing
      if (job.status === 'completed' && 
          !invoices.some((inv: { customerId: string; issueDate: string; }) => 
            inv.customerId === job.customer?.id && inv.issueDate === job.date_time)) {
        counts.needs_invoicing += 1;
      }

      return true;
    });

    const filtered = matches.filter((job) => {
      switch (statusFilter) {
        case 'all':
          return true;
        case 'needs_invoicing':
          return job.status === 'completed' && 
            !invoices.some((inv: { customerId: string; issueDate: string; }) => inv.customerId === job.customer?.id && inv.issueDate === job.date_time);
        case 'overdue': {
          const jobDate = new Date(job.date_time);
          const today = new Date();
          return jobDate < today && !['completed', 'cancelled'].includes(job.status);
        }
        default:
          return job.status === statusFilter;
      }
    });

    return { filteredJobs: filtered, filterCounts: counts };
  }, [jobs, deferredSearchTerm, statusFilter, invoices]);

  const sortedJobs = useMemo(() => {
    return [...filteredJobs].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'customer':
          aValue = a.customer?.name || '';
          bValue = b.customer?.name || '';
          break;
        case 'address':
          aValue = a.property?.addressLine1 || '';
          bValue = b.property?.addressLine1 || '';
          break;
        case 'business':
          aValue = a.business?.businessName || '';
          bValue = b.business?.businessName || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'price':
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case 'time':
        default:
          aValue = new Date(a.date_time).getTime();
          bValue = new Date(b.date_time).getTime();
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredJobs, sortColumn, sortDirection]);
  console.log('Filtered Jobs:', filteredJobs);

  const totalJobs = sortedJobs.length;
  const totalPages = Math.ceil(totalJobs / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentJobs = sortedJobs.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Job status filters
  const JOB_FILTERS = useMemo(() => [
    { 
      value: 'all', 
      label: 'All Jobs', 
      count: filterCounts.all 
    },
    { 
      value: 'requested', 
      label: 'Requested', 
      count: filterCounts.requested 
    },
    { 
      value: 'scheduled', 
      label: 'Scheduled', 
      count: filterCounts.scheduled 
    },
    { 
      value: 'in-progress', 
      label: 'In Progress', 
      count: filterCounts['in-progress'] 
    },
    { 
      value: 'completed', 
      label: 'Completed', 
      count: filterCounts.completed 
    },
    { 
      value: 'cancelled', 
      label: 'Cancelled', 
      count: filterCounts.cancelled 
    },
    { 
      value: 'overdue', 
      label: 'Overdue', 
      count: filterCounts.overdue 
    },
    { 
      value: 'needs_invoicing', 
      label: 'Needs Invoice', 
      count: filterCounts.needs_invoicing 
    }
  ], [filterCounts]);

  // Memoized event handlers for better performance
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  }, []);

  const handleFilterChange = useCallback((value: string) => {
    setStatusFilter(value);
    setCurrentPage(1); // Reset to first page when filtering
  }, []);

  const handleJobClick = useCallback((jobId: string) => {
    navigate(`/jobs/${jobId}`);
  }, [navigate]);

  const handleCreateJob = useCallback(() => {
    navigate('/jobs/create');
  }, [navigate]);

  const handleSort = useCallback((column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn, sortDirection]);

  return (
    <div className="space-y-6">
      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading jobs...</div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Jobs</h1>
              <p className="text-gray-600 mt-1">Jobs reflect committed work and progress through various stages</p>
            </div>
            {permissions.canEditJobs && (
              <button
                onClick={handleCreateJob}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-indigo-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>New Job</span>
              </button>
            )}
          </div>

          {/* Filters and Search */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex flex-col space-y-4">
              {/* Search Bar */}
              <SearchBar
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search jobs by customer, service, address, or business..."
              />

              {/* Job Status Filters */}
              <StatusFilters
                filters={JOB_FILTERS}
                selectedFilter={statusFilter}
                onFilterChange={handleFilterChange}
              />
            </div>
          </div>

          {/* Jobs Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {currentJobs.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <Search className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
                <p className="text-gray-500">
                  {searchTerm || statusFilter !== 'all'
                    ? "No jobs match your current filters. Try adjusting your search or filter criteria."
                    : "No jobs have been created yet."
                  }
                </p>
                {permissions.canEditJobs && !searchTerm && statusFilter === 'all' && (
                  <button
                    onClick={handleCreateJob}
                    className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Create First Job
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <SortableHeader 
                          label="Date & Time"
                          column="time"
                          currentColumn={sortColumn}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader 
                          label="Customer"
                          column="customer"
                          currentColumn={sortColumn}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader 
                          label="Assigned Business"
                          column="business"
                          currentColumn={sortColumn}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader 
                          label="Status"
                          column="status"
                          currentColumn={sortColumn}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader 
                          label="Value"
                          column="price"
                          currentColumn={sortColumn}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentJobs.map((job) => {
                        const isOverdue = new Date(job.date_time) < new Date() && !['completed', 'cancelled'].includes(job.status);
                        const needsInvoicing = job.status === 'completed' && !invoices.some((inv: { customerId: string; issueDate: string; }) => inv.customerId === job.customer?.id && inv.issueDate === job.date_time);

                        return (
                          <tr 
                            key={job.id} 
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleJobClick(job.id)}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {DateUtils.formatDateLong(job.date_time)}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {DateUtils.formatTimeFromDate(job.date_time)}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {job.duration} min
                                </div>
                              </div>
                            </td>
                            
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-start">
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">
                                    {job.customer?.name}
                                  </div>
                                  <div className="text-sm text-gray-500 flex items-center">
                                    <span className="break-words">{job.property?.addressLine1}</span>
                                  </div>
                                  {job.property && (
                                    <div className="text-xs text-gray-400">
                                      {job.property?.addressLine1} • {job.property?.bedrooms}br/{job.property?.bathrooms}ba
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="text-sm font-medium text-gray-900">
                                  {job.business?.businessName}
                                </div>
                              </div>
                            </td>
                            
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col space-y-1">
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-lg ${getBadgeColorClasses(getStatusColor(job.status, 'job'))}`}>
                                  {job.status === 'in-progress' ? 'In Progress' : 
                                   job.status === 'requested' ? 'Requested' :
                                   job.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                                {isOverdue && (
                                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-lg bg-red-100 text-red-800">
                                    Overdue
                                  </span>
                                )}
                                {needsInvoicing && (
                                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-lg bg-orange-100 text-orange-800">
                                    Needs Invoice
                                  </span>
                                )}
                              </div>
                            </td>
                            
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 flex items-center">
                                {NumberUtils.formatCurrency(job.price)}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalJobs}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setCurrentPage}
                />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}